import { Link } from 'react-router-dom';
import { LANDING_FAQ } from '../content/landingFaq.js';

/**
 * What a signed-out visitor sees.
 *
 * It carries the sign-in buttons rather than replacing them: this is the same
 * screen either way, so there is one public URL and no marketing site to keep
 * in step with the app. It exists because a bare "Sign in to reach this pantry"
 * card tells a first-time visitor -- or anyone reviewing the domain -- nothing
 * about what the product is.
 *
 * Everything claimed here is something the app actually does. Adding a feature
 * to this list before it ships makes the page a liability rather than an asset.
 */
export default function Landing({ children }) {
  return (
    <div className="landing">
      <header className="landing-hero">
        <div className="landing-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <rect x="4" y="3" width="16" height="18" rx="2" />
            <path d="M4 12h16" />
            <path d="M9 7.5h1" />
            <path d="M9 16.5h1" />
          </svg>
        </div>
        <h1>Know what is in your kitchen.</h1>
        <p className="landing-lede">
          Pantry Pro keeps a real inventory of your food — what you have, where it is, and
          when it runs out — from a photograph of a receipt or a shelf. Then it tells you
          what you can cook tonight without going to the store.
        </p>

        <div className="landing-actions">{children}</div>
      </header>

      <section className="landing-section">
        <h2>What it does</h2>
        <div className="landing-grid">
          <Feature title="Receipts become inventory">
            Photograph a receipt and the lines are read, matched against your item list, and
            staged for review. Nothing is written to your pantry until you approve it, because
            a misread line on a permanent list is expensive to undo.
          </Feature>
          <Feature title="Shelf scans reconcile it">
            Photograph a shelf and it is compared against what the pantry thinks is there, so
            the count stays honest without anyone typing anything in.
          </Feature>
          <Feature title="Barcodes, on the device">
            Scanning a barcode is decoded in the browser — the image never leaves your phone —
            and looked up for nutrition and product data.
          </Feature>
          <Feature title="Recipes from anywhere">
            Import a recipe from a link, a photograph of a cookbook page, or pasted text. The
            ingredients are parsed into quantities and units your pantry can reason about.
          </Feature>
          <Feature title="Cook from what you have">
            Ask what is makeable right now and get recipes ranked by what is already in the
            house, with the missing pieces listed rather than hidden.
          </Feature>
          <Feature title="Lists and plans that know the pantry">
            Shopping lists fill themselves from low stock, meal plans and recipe gaps, so the
            list is what you actually need rather than what you remembered.
          </Feature>
        </div>
      </section>

      <section className="landing-section">
        <h2>How it works</h2>
        <ol className="landing-steps">
          <li>
            <strong>Photograph or scan.</strong> A receipt, a shelf, a barcode, a cookbook page.
          </li>
          <li>
            <strong>Review what it read.</strong> Extraction is good, not perfect, so it lands as
            a draft you confirm — never straight into your inventory.
          </li>
          <li>
            <strong>Cook, plan, and shop from it.</strong> The inventory drives what is makeable,
            what is running low, and what ends up on the list.
          </li>
        </ol>
        <p className="small muted">
          Photographs are analyzed by an AI model to read them. What that means for your data is
          set out in the <Link to="/privacy">privacy policy</Link>.
        </p>
      </section>

      {/*
        The one number on this page that is not a description of the app, and it
        is the EPA's rather than ours: April 2025, $2,913 a year for a household
        of four, an average of $56 a week. Two percent of $2,913 is $58.26, and a
        year of Pantry Pro is $50. The rule the rest of this page follows applies
        here too -- if the figure changes, this paragraph changes with it.

        Plain anchors, not <Link>. /guides/ and the articles under it are
        prerendered documents served as files, not client routes, so a <Link>
        would hand them to the router, which has no such route and would answer
        with the app's catch-all instead of the page.
      */}
      <section className="landing-section">
        <h2>Why it pays for itself</h2>
        {/*
          Dimmed like every other block of prose on this page. Left bare it
          inherited the full text colour from body, which made the one
          paragraph on the page arguing for the price the darkest text on it.
        */}
        <p className="muted">
          By the EPA&rsquo;s April 2025 estimate, a household of four throws away about $2,900 of
          food a year — around $56 a week. Preventing two percent of that pays for a year of
          Pantry Pro. <a href="/guides/what-food-waste-costs-a-household/">What food waste costs
          a household</a> shows the arithmetic and cites the report it comes from.
        </p>
      </section>

      {/*
        The same list the page's FAQPage structured data is written from, so the
        two cannot disagree. Plain h3/p rather than <details>: an answer that is
        collapsed by default is an answer a text-only reader never sees.
      */}
      <section className="landing-section">
        <h2>Questions people ask</h2>
        {LANDING_FAQ.map(({ question, answer }) => (
          <div key={question} className="landing-faq">
            <h3>{question}</h3>
            <p className="muted">{answer}</p>
          </div>
        ))}
      </section>
      <footer className="landing-footer">
        <nav className="landing-links">
          <a href="/guides/">Guides</a>
          <Link to="/help">Manual</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
        <p className="tiny muted">Pantry Pro</p>
      </footer>
    </div>
  );
}

function Feature({ title, children }) {
  return (
    <div className="landing-feature">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
